import React from 'react';
import * as Sentry from "@sentry/react";

/**
 * ErrorBoundary that catches harmless DOM manipulation errors
 * (like removeChild, insertBefore, appendChild) and allows the app
 * to continue running instead of crashing.
 *
 * These errors typically occur due to conflicts between React's
 * virtual DOM and third-party libraries (MDB, framer-motion, etc.)
 * manipulating the real DOM.
 */
class DOMErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false, isDOMError: false };
    this.lastErrorTime = 0;
    this.errorCount = 0;
  }

  static getDerivedStateFromError(error) {
    const isDOMError = error?.message?.includes('removeChild') ||
                       error?.message?.includes('insertBefore') ||
                       error?.message?.includes('appendChild');

    return {
      hasError: true,
      isDOMError
    };
  }

  componentDidCatch(error, errorInfo) {
    const now = Date.now();

    if (this.state.isDOMError) {
      // Check if this is a repeated error (within 2 seconds) - indicates a loop
      if (now - this.lastErrorTime < 2000) {
        this.errorCount++;
      } else {
        this.errorCount = 1;
      }
      this.lastErrorTime = now;

      console.warn('Harmless DOM error caught and ignored:', error.message);

      // Only send to Sentry once per error burst (not every retry)
      if (this.errorCount === 1) {
        Sentry.captureException(error, {
          level: 'warning',
          tags: { handled: 'dom-error-boundary' },
          extra: errorInfo
        });
      }

      // If we've seen this error 3+ times quickly, stop retrying to prevent loop
      if (this.errorCount >= 3) {
        console.warn('DOM error loop detected, stopping retries');
        // Just clear the error state and continue without remounting
        setTimeout(() => {
          this.setState({ hasError: false, isDOMError: false });
        }, 0);
      } else {
        // Normal retry - just clear error state
        setTimeout(() => {
          this.setState({ hasError: false, isDOMError: false });
        }, 0);
      }
    } else {
      Sentry.captureException(error, { extra: errorInfo });
    }
  }

  render() {
    // For non-DOM errors, show fallback
    if (this.state.hasError && !this.state.isDOMError) {
      return this.props.fallback;
    }
    // For DOM errors, just continue rendering (error will clear shortly)
    if (this.state.hasError && this.state.isDOMError) {
      return this.props.children;
    }
    return this.props.children;
  }
}

export default DOMErrorBoundary;
